/**
 * Client-side checks for resource uploads.
 *
 * A convenience, NOT a security boundary. The server re-validates everything and
 * additionally sniffs magic bytes, which the browser cannot do cheaply — so a
 * file that passes here can still be rejected with a 400, and that response is
 * the authoritative one. The point of these checks is to fail obviously wrong
 * files instantly instead of after a 10MB upload.
 *
 * Kept separate from utils/imageProcessing.ts (`validateImageFile`), which is
 * hard-wired to JPEG/PNG at 5MB for avatars and would reject every document.
 */

export const MAX_RESOURCE_SIZE_MB = 10;
const MAX_RESOURCE_SIZE_BYTES = MAX_RESOURCE_SIZE_MB * 1024 * 1024;

/** Extensions the server accepts. Keep in step with ALLOWED_TYPES in
 *  app/services/file_validation.py. */
export const ALLOWED_RESOURCE_EXTENSIONS = [
  "pdf",
  "docx",
  "xlsx",
  "csv",
  "txt",
  "html",
  "md",
  "jpg",
  "jpeg",
  "png",
] as const;

/** Value for an <input type="file"> accept attribute. */
export const RESOURCE_ACCEPT_ATTR = ALLOWED_RESOURCE_EXTENSIONS.map((e) => `.${e}`).join(",");

export function getFileExtension(filename: string): string {
  const idx = filename.lastIndexOf(".");
  return idx === -1 ? "" : filename.slice(idx + 1).toLowerCase();
}

/**
 * Returns null when the file looks acceptable, otherwise a message to show.
 *
 * Returns rather than throws because uploads are multi-file: one bad file in a
 * dropped batch should be reported and skipped, not abort the whole batch.
 */
export function validateResourceFile(file: File): string | null {
  const ext = getFileExtension(file.name);

  if (!ext) {
    return `"${file.name}" has no file extension.`;
  }
  if (!(ALLOWED_RESOURCE_EXTENSIONS as readonly string[]).includes(ext)) {
    return `"${file.name}" isn't a supported file type. Allowed: ${ALLOWED_RESOURCE_EXTENSIONS.join(", ")}.`;
  }
  if (file.size === 0) {
    return `"${file.name}" is empty.`;
  }
  if (file.size > MAX_RESOURCE_SIZE_BYTES) {
    return `"${file.name}" is ${formatFileSize(file.size)}. The limit is ${MAX_RESOURCE_SIZE_MB} MB.`;
  }
  return null;
}

/**
 * Human file size, matching the design's "420 KB" / "1.8 MB".
 *
 * Uses 1024-based units with decimal labels, which is what every OS file browser
 * shows — users compare this against what their machine told them, not against
 * a strict SI definition.
 */
export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
}
